
import { defineComponent, nextTick } from 'vue'
import {
  ExclamationCircleOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons-vue'
import { notification, message } from 'ant-design-vue'
import dayjs from 'dayjs'

import { EnvelopeService } from '@/services/EnvelopeService'
import { ListEnvelope } from '@/services/responses/ListEnvelopeResponse'
import { eStatusEnvelope } from '@/services/enums/eStatusEnvelope'

export default defineComponent({
  name: 'DeleteEnvelopeModal',

  components: {
    ExclamationCircleOutlined,
    FilePdfOutlined,
    CheckCircleFilled,
    CloseCircleFilled
  },

  props: {
    visible: { type: Boolean, required: true },
    envelope: { type: Object as () => ListEnvelope | null, default: null }
  },

  emits: ['update:visible', 'deleted'],

  data () {
    return {
      isLoadingDetails: false,
      isDeleting: false,
      signatories: [] as any[],
      requestId: 0,
      confirmationSeconds: 5,
      confirmationTimer: null as number | null
    }
  },

  computed: {
    isCanceled (): boolean {
      return this.envelope?.status === eStatusEnvelope.CANCELED
    },
    formattedCreationDate (): string {
      const date = this.envelope?.date
      if (!date) return 'Data não informada'
      const parsed = dayjs(date)
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(date)
    }
  },

  watch: {
    visible (isVisible: boolean) {
      if (isVisible) {
        this.loadSignatories()
        // pequeno atraso: o modal do ant-design 2.x monta o DOM depois do nextTick
        nextTick(() => setTimeout(() => this.focusBackButton(), 100))
      } else {
        this.requestId++
        this.clearConfirmationCountdown()
        this.confirmationSeconds = 5
        this.isLoadingDetails = false
        this.isDeleting = false
      }
    }
  },

  methods: {
    async loadSignatories () {
      if (!this.envelope?.hashId) {
        this.signatories = []
        return
      }

      const currentRequest = ++this.requestId
      this.isLoadingDetails = true
      this.signatories = []

      try {
        const details = await EnvelopeService
          .getInstance()
          .detailsEnvelope(this.envelope.hashId)

        if (currentRequest !== this.requestId || !this.visible) return

        const requests = Array.isArray(details?.signatureRequests)
          ? details.signatureRequests
          : []

        // Observadores (status 5) não assinam; não entram na lista.
        this.signatories = requests.filter((s: any) => s.status !== 5)
      } catch (error) {
        if (currentRequest !== this.requestId || !this.visible) return
        notification.error({
          message: 'Erro ao carregar o documento',
          description: 'Não foi possível carregar os signatários. Tente novamente.'
        })
      } finally {
        if (
          currentRequest === this.requestId &&
          this.visible
        ) {
          this.isLoadingDetails = false
          this.startConfirmationCountdown()
        }
      }
    },

    getInitials (name?: string): string {
      if (!name?.trim()) return '?'
      return name.trim().split(/\s+/).slice(0, 2)
        .map(p => p.charAt(0)).join('').toUpperCase()
    },

    getSignatureDate (signatory: any): string {
      const parsed = signatory.signedAt ? dayjs(signatory.signedAt) : null
      return parsed?.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : 'Data não informada'
    },

    focusBackButton () {
      const button = this.$refs.backButton as any
      if (button?.$el instanceof HTMLElement) button.$el.focus()
    },

    closeModal () {
      if (this.isDeleting) return
      this.$emit('update:visible', false)
    },

    async confirmDeletion () {
      if (!this.envelope || this.isDeleting || this.isLoadingDetails) return

      this.isDeleting = true
      try {
        await EnvelopeService.getInstance().removeEnvelope(this.envelope.id)

        notification.success({
            message: 'Documento excluído com sucesso',
            placement: 'topRight',
            duration: 4,
            class: 'notification-success-green'
        })
        this.$emit('deleted', this.envelope)
        this.$emit('update:visible', false)
      } catch (error) {
        notification.error({
          message: 'Falha ao excluir documento',
          description: 'Não foi possível excluir o documento. Tente novamente.'
        })
      } finally {
        this.isDeleting = false
      }
    },
    startConfirmationCountdown () {
      this.clearConfirmationCountdown()
      this.confirmationSeconds = 5

      this.confirmationTimer = setInterval(() => {
        if (this.confirmationSeconds <= 1) {
          this.confirmationSeconds = 0
          this.clearConfirmationCountdown()
          return
        }

        this.confirmationSeconds--
      }, 1000) as unknown as number
    },
    clearConfirmationCountdown () {
      if (this.confirmationTimer !== null) {
        clearInterval(this.confirmationTimer)
        this.confirmationTimer = null
      }
    }
  }
})
